import { isQualifiedEvidence, type ExerciseAttempt } from '@/entities/attempt'
import { getDynamicEvidenceWeight } from '@/entities/profile/createDynamicAssessmentSnapshot'
import { getBaselineLearningRatios } from '@/engine/calculateLearningRatios'
import type {
  GeneratedLearningPlan,
  GeneratedTask,
  LearningDimensionRatios,
  LearningLayer,
  SkillDimension,
} from '@/types'

export type LearningReviewStatus = 'not-started' | 'in-progress' | 'complete'

export interface DailyLearningSummary {
  date: string
  dayIndex: number
  weekIndex: number
  status: LearningReviewStatus
  plannedTaskCount: number
  completedTaskCount: number
  carryOverTaskCount: number
  attemptCount: number
  correctCount: number
  accuracy: number | null
  durationMs: number
  hintCount: number
  retryCount: number
  coveredDimensions: SkillDimension[]
  strongDimension: SkillDimension | null
  supportDimension: SkillDimension | null
  completedTaskNames: string[]
  plannedTaskNames: string[]
  tomorrowTaskName: string | null
  childSummary: {
    headline: string
    completed: string
    strong: string
    support: string
    next: string
  }
}

export interface WeeklyLearningReview {
  weekIndex: number
  weekStart: string
  weekEnd: string
  status: LearningReviewStatus
  plannedTaskCount: number
  completedTaskCount: number
  completionRate: number
  attemptCount: number
  correctCount: number
  accuracy: number | null
  independence: number | null
  coveredDimensions: SkillDimension[]
  strongDimensions: SkillDimension[]
  supportDimensions: SkillDimension[]
  sufficientEvidence: boolean
  summary: string
}

export interface WeeklyAdjustment {
  appliedToWeekIndex: number
  evidenceSufficient: boolean
  levelBefore: LearningLayer
  recommendedLevel: LearningLayer
  levelDelta: -1 | 0 | 1
  ratiosBefore: LearningDimensionRatios
  recommendedRatios: LearningDimensionRatios
  changed: boolean
  reason: string
}

export interface LearningReviewSnapshot {
  today: DailyLearningSummary
  currentWeek: WeeklyLearningReview
  previousWeek: WeeklyLearningReview | null
  adjustment: WeeklyAdjustment
  /** One deterministic report per calendar day, bounded to the latest twelve weeks. */
  dailyHistory: DailyLearningSummary[]
  weeklyHistory: WeeklyLearningReview[]
}

export interface LearningReviewInput {
  studentId: string
  plan: GeneratedLearningPlan
  weeklyPlanStartDate: string | null
  attempts: readonly ExerciseAttempt[]
  completedTasks: Readonly<Record<string, boolean>>
  taskCompletedAt?: Readonly<Record<string, string>>
  now?: Date | string
}

const DIMENSIONS: SkillDimension[] = ['listening', 'speaking', 'reading', 'writing']
const DAY_MS = 24 * 60 * 60 * 1000
const MIN_WEEKLY_ATTEMPTS = 8
const MIN_WEEKLY_DIMENSIONS = 2

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/** Convert a timestamp to a local calendar date without shifting date-only values. */
export function toCalendarDate(value: Date | string): string | null {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const date = value instanceof Date ? value : new Date(value)
  if (!Number.isFinite(date.getTime())) return null
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function dateAtStart(value: string): number {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day).getTime()
}

export function addCalendarDays(date: string, days: number): string {
  const result = new Date(dateAtStart(date))
  result.setDate(result.getDate() + days)
  return toCalendarDate(result) as string
}

export function getPersonalDayIndex(startDate: string, value: Date | string): number | null {
  const target = toCalendarDate(value)
  const start = toCalendarDate(startDate)
  if (!target || !start) return null
  return Math.floor((dateAtStart(target) - dateAtStart(start)) / DAY_MS)
}

export function getPersonalWeekIndex(startDate: string, value: Date | string): number | null {
  const dayIndex = getPersonalDayIndex(startDate, value)
  return dayIndex === null ? null : Math.floor(dayIndex / 7)
}

function taskId(unitId: string, index: number): string {
  return `plan:${unitId}:task:${index + 1}`
}

function isCoreTask(task: GeneratedTask): boolean {
  return task.category === 'core-learning' || task.category === 'practice'
}

interface IndexedTask {
  task: GeneratedTask
  index: number
  weekIndex: number
  dayIndex: number
  id: string
}

function indexPlanTasks(plan: GeneratedLearningPlan): IndexedTask[] {
  const indexed: IndexedTask[] = []
  let globalIndex = 0
  plan.weeklyPlans.forEach((week, weekIndex) => {
    week.tasks.forEach((task) => {
      if (isCoreTask(task)) {
        indexed.push({ task, index: globalIndex, weekIndex, dayIndex: weekIndex * 7 + indexed.filter((item) => item.weekIndex === weekIndex).length, id: taskId(plan.currentUnitId, globalIndex) })
      }
      globalIndex += 1
    })
  })
  return indexed
}

function isCompleted(
  item: IndexedTask,
  completedTasks: Readonly<Record<string, boolean>>,
  taskCompletedAt: Readonly<Record<string, string>>,
): boolean {
  return Boolean(completedTasks[item.id] || taskCompletedAt[item.id])
}

function completionDate(
  item: IndexedTask,
  taskCompletedAt: Readonly<Record<string, string>>,
): string | null {
  return toCalendarDate(taskCompletedAt[item.id] ?? '')
}

function wasCompletedBefore(
  item: IndexedTask,
  date: string,
  completedTasks: Readonly<Record<string, boolean>>,
  taskCompletedAt: Readonly<Record<string, string>>,
): boolean {
  const completedOn = completionDate(item, taskCompletedAt)
  if (completedOn) return completedOn < date
  return Boolean(completedTasks[item.id])
}

function attemptsForDate(
  attempts: readonly ExerciseAttempt[],
  studentId: string,
  date: string,
): ExerciseAttempt[] {
  return attempts.filter((attempt) => (
    attempt.studentId === studentId
    && isQualifiedEvidence(attempt)
    && attempt.evidenceKind !== 'remediation-training'
    && toCalendarDate(attempt.completedAt) === date
  ))
}

function attemptsForWeek(
  attempts: readonly ExerciseAttempt[],
  studentId: string,
  weekStart: string,
): ExerciseAttempt[] {
  const weekEnd = addCalendarDays(weekStart, 7)
  return attempts.filter((attempt) => {
    const date = toCalendarDate(attempt.completedAt)
    return Boolean(
      date
      && date >= weekStart
      && date < weekEnd
      && attempt.studentId === studentId
      && isQualifiedEvidence(attempt)
      && attempt.evidenceKind !== 'remediation-training',
    )
  })
}

function averageScore(attempts: readonly ExerciseAttempt[]): number | null {
  if (attempts.length === 0) return null
  const weight = attempts.reduce((sum, attempt) => sum + getDynamicEvidenceWeight(attempt), 0)
  if (weight === 0) return null
  return Math.round(attempts.reduce((sum, attempt) => (
    sum + (typeof attempt.score === 'number' ? attempt.score : attempt.correct ? 100 : 0) * getDynamicEvidenceWeight(attempt)
  ), 0) / weight)
}

function dimensionsFromAttempts(attempts: readonly ExerciseAttempt[]): SkillDimension[] {
  return DIMENSIONS.filter((dimension) => attempts.some((attempt) => attempt.ability === dimension))
}

function dimensionAccuracy(attempts: readonly ExerciseAttempt[], dimension: SkillDimension): number | null {
  return averageScore(attempts.filter((attempt) => attempt.ability === dimension))
}

function rankDimensions(attempts: readonly ExerciseAttempt[], ascending: boolean): SkillDimension[] {
  return DIMENSIONS
    .map((dimension) => ({ dimension, score: dimensionAccuracy(attempts, dimension), count: attempts.filter((attempt) => attempt.ability === dimension).length }))
    .filter((item) => item.score !== null && item.count > 0)
    .sort((left, right) => {
      const scoreDelta = (left.score as number) - (right.score as number)
      return (ascending ? scoreDelta : -scoreDelta) || right.count - left.count
    })
    .map((item) => item.dimension)
}

function taskNames(items: IndexedTask[]): string[] {
  return items.map((item) => item.task.taskType)
}

function createChildSummary(
  status: LearningReviewStatus,
  completedCount: number,
  plannedCount: number,
  accuracy: number | null,
  strongDimension: SkillDimension | null,
  supportDimension: SkillDimension | null,
  tomorrowTaskName: string | null,
): DailyLearningSummary['childSummary'] {
  const labels: Record<SkillDimension, string> = {
    listening: '听力', speaking: '口语', reading: '阅读', writing: '写作',
  }
  const headline = status === 'not-started'
    ? '今天还没有开始'
    : status === 'complete' ? '今天的学习完成啦' : '今天已经走出第一步'
  return {
    headline,
    completed: status === 'not-started'
      ? '完成今天的第一项任务，就算正式出发。'
      : `今天完成了 ${completedCount}/${plannedCount || 1} 项计划任务${accuracy === null ? '' : `，正确率 ${accuracy}%`}。`,
    strong: strongDimension ? `今天的${labels[strongDimension]}表现很稳。` : '再完成几次练习，就能看见你的优势。',
    support: supportDimension ? `明天可以多练一会儿${labels[supportDimension]}。` : '继续保持，系统会用更多证据帮你校准路线。',
    next: tomorrowTaskName ? `明天先做：${tomorrowTaskName}` : '明天继续完成一项小任务。',
  }
}

export function createDailyLearningSummary(input: LearningReviewInput, dateInput?: Date | string): DailyLearningSummary {
  const startDate = toCalendarDate(input.weeklyPlanStartDate ?? '') ?? toCalendarDate(input.now ?? new Date()) as string
  const date = toCalendarDate(dateInput ?? input.now ?? new Date()) as string
  const dayIndex = Math.max(0, getPersonalDayIndex(startDate, date) ?? 0)
  const weekIndex = Math.floor(dayIndex / 7)
  const allTasks = indexPlanTasks(input.plan)
  const taskCompletedAt = input.taskCompletedAt ?? {}
  const due = allTasks.filter((item) => item.dayIndex <= dayIndex)
  const todayItems = allTasks.filter((item) => item.dayIndex === dayIndex)
  const carryOver = due.filter((item) => (
    item.dayIndex < dayIndex
    && !wasCompletedBefore(item, date, input.completedTasks, taskCompletedAt)
  ))
  const planned = [...carryOver, ...todayItems.filter((item) => !carryOver.some((carry) => carry.id === item.id))]
  const attempts = attemptsForDate(input.attempts, input.studentId, date)
  const correctCount = attempts.filter((attempt) => attempt.correct).length
  const accuracy = averageScore(attempts)
  const completedToday = allTasks.filter((item) => completionDate(item, taskCompletedAt) === date)
  const hasAnyWork = attempts.length > 0 || completedToday.length > 0
  const status: LearningReviewStatus = !hasAnyWork
    ? 'not-started'
    : planned.length === 0 || planned.every((item) => isCompleted(item, input.completedTasks, taskCompletedAt))
      ? 'complete'
      : 'in-progress'
  const ranked = rankDimensions(attempts, false)
  const weak = rankDimensions(attempts, true)
  const next = allTasks.find((item) => item.dayIndex === dayIndex + 1) ?? null

  return {
    date,
    dayIndex,
    weekIndex,
    status,
    plannedTaskCount: planned.length,
    completedTaskCount: completedToday.length,
    carryOverTaskCount: carryOver.length,
    attemptCount: attempts.length,
    correctCount,
    accuracy,
    durationMs: attempts.reduce((sum, attempt) => sum + attempt.durationMs, 0),
    hintCount: attempts.reduce((sum, attempt) => sum + attempt.hintCount, 0),
    retryCount: attempts.reduce((sum, attempt) => sum + attempt.retryCount, 0),
    coveredDimensions: dimensionsFromAttempts(attempts),
    strongDimension: ranked[0] ?? null,
    supportDimension: weak[0] ?? null,
    completedTaskNames: taskNames(completedToday),
    plannedTaskNames: taskNames(planned),
    tomorrowTaskName: next?.task.taskType ?? null,
    childSummary: createChildSummary(status, completedToday.length, planned.length, accuracy, ranked[0] ?? null, weak[0] ?? null, next?.task.taskType ?? null),
  }
}

export function createWeeklyLearningReview(input: LearningReviewInput, weekIndex: number): WeeklyLearningReview {
  const startDate = toCalendarDate(input.weeklyPlanStartDate ?? '') ?? toCalendarDate(input.now ?? new Date()) as string
  const weekStart = addCalendarDays(startDate, Math.max(0, weekIndex) * 7)
  const weekEnd = addCalendarDays(weekStart, 7)
  const allTasks = indexPlanTasks(input.plan).filter((item) => item.weekIndex === weekIndex)
  const taskCompletedAt = input.taskCompletedAt ?? {}
  const completed = allTasks.filter((item) => {
    const completedOn = completionDate(item, taskCompletedAt)
    if (!completedOn) return Boolean(input.completedTasks[item.id])
    return completedOn >= weekStart && completedOn < weekEnd
  })
  const attempts = attemptsForWeek(input.attempts, input.studentId, weekStart)
  const levelEvidence = attempts
  const correctCount = attempts.filter((attempt) => attempt.correct).length
  const accuracy = averageScore(levelEvidence)
  const coveredDimensions = dimensionsFromAttempts(levelEvidence)
  const independence = levelEvidence.length === 0 ? null : Math.round(levelEvidence.reduce((sum, attempt) => sum + getDynamicEvidenceWeight(attempt), 0) / levelEvidence.length * 100)
  const sufficientEvidence = levelEvidence.length >= MIN_WEEKLY_ATTEMPTS && coveredDimensions.length >= MIN_WEEKLY_DIMENSIONS
  const status: LearningReviewStatus = completed.length === 0 && attempts.length === 0
    ? 'not-started'
    : completed.length >= allTasks.length && allTasks.length > 0
      ? 'complete'
      : 'in-progress'
  const strongDimensions = rankDimensions(levelEvidence, false).filter((dimension) => (dimensionAccuracy(levelEvidence, dimension) ?? 0) >= 80).slice(0, 2)
  const supportDimensions = rankDimensions(levelEvidence, true).filter((dimension) => (dimensionAccuracy(levelEvidence, dimension) ?? 100) < 70).slice(0, 2)

  return {
    weekIndex,
    weekStart,
    weekEnd,
    status,
    plannedTaskCount: allTasks.length,
    completedTaskCount: completed.length,
    completionRate: allTasks.length === 0 ? 0 : Math.round((completed.length / allTasks.length) * 100),
    attemptCount: attempts.length,
    correctCount,
    accuracy,
    independence,
    coveredDimensions,
    strongDimensions,
    supportDimensions,
    sufficientEvidence,
    summary: status === 'not-started'
      ? '这一周还没有学习记录。'
      : sufficientEvidence
        ? `本周完成 ${completed.length}/${allTasks.length} 项任务，覆盖 ${coveredDimensions.length} 个能力方向。`
        : '本周证据还在积累，先继续完成计划，不急着改变学习路线。',
  }
}

function moveLevel(level: LearningLayer, delta: -1 | 0 | 1): LearningLayer {
  if (delta === 0) return level
  if (delta > 0) return level === 'C' ? 'B' : 'A'
  return level === 'A' ? 'B' : 'C'
}

export function createWeeklyAdjustment(
  currentWeek: WeeklyLearningReview,
  levelBefore: LearningLayer,
  ratiosBefore: LearningDimensionRatios,
  nextWeekIndex = currentWeek.weekIndex + 1,
): WeeklyAdjustment {
  let levelDelta: -1 | 0 | 1 = 0
  if (currentWeek.sufficientEvidence && (currentWeek.accuracy ?? 0) >= 85 && currentWeek.completionRate >= 70 && (currentWeek.independence ?? 0) >= 65) levelDelta = 1
  if (currentWeek.sufficientEvidence && (currentWeek.accuracy ?? 100) <= 50 && currentWeek.completionRate >= 40) levelDelta = -1
  const recommendedLevel = moveLevel(levelBefore, levelDelta)
  const nextLevelBaseRatios = levelDelta === 0 ? ratiosBefore : getBaselineLearningRatios(recommendedLevel)
  const ratiosChanged = currentWeek.sufficientEvidence && currentWeek.supportDimensions.length > 0
  const recommendedRatios = ratiosChanged
    ? adjustRatios(nextLevelBaseRatios, currentWeek.supportDimensions, currentWeek.strongDimensions)
    : { ...nextLevelBaseRatios }
  const changed = levelDelta !== 0 || Object.keys(ratiosBefore).some((key) => ratiosBefore[key as SkillDimension] !== recommendedRatios[key as SkillDimension])
  const reason = !currentWeek.sufficientEvidence
    ? '证据还不够，当前只记录表现，不改变本单元等级或题目比例。'
    : changed
      ? `本阶段过程正确率 ${currentWeek.accuracy ?? 0}%、完成率 ${currentWeek.completionRate}% 和能力覆盖已记录，将在单元结束时统一参与个性化题量判断。`
      : '本阶段过程表现稳定，当前单元继续沿用已冻结的等级和题目比例。'
  return { appliedToWeekIndex: nextWeekIndex, evidenceSufficient: currentWeek.sufficientEvidence, levelBefore, recommendedLevel, levelDelta, ratiosBefore, recommendedRatios, changed, reason }
}

function adjustRatios(
  base: LearningDimensionRatios,
  support: readonly SkillDimension[],
  strong: readonly SkillDimension[],
): LearningDimensionRatios {
  const result = { ...base }
  const donors = DIMENSIONS.filter((dimension) => !support.includes(dimension)).sort((left, right) => {
    const leftStrong = strong.includes(left) ? 1 : 0
    const rightStrong = strong.includes(right) ? 1 : 0
    return rightStrong - leftStrong || result[right] - result[left]
  })
  support.slice(0, 2).forEach((dimension) => {
    const boost = support.length === 1 ? 10 : 5
    result[dimension] += boost
    let remaining = boost
    for (const donor of donors) {
      if (remaining <= 0) break
      const reduction = Math.min(5, remaining, Math.max(0, result[donor] - 5))
      result[donor] -= reduction
      remaining -= reduction
    }
  })
  return result
}

export function createLearningReview(input: LearningReviewInput): LearningReviewSnapshot | null {
  if (!input.weeklyPlanStartDate) return null
  const now = input.now ?? new Date()
  const today = createDailyLearningSummary(input, now)
  const currentWeek = createWeeklyLearningReview(input, today.weekIndex)
  const previousWeek = today.weekIndex > 0 ? createWeeklyLearningReview(input, today.weekIndex - 1) : null
  const ratiosBefore = input.plan.dimensionRatios
  const adjustment = previousWeek
    ? createWeeklyAdjustment(previousWeek, input.plan.layer, ratiosBefore, today.weekIndex)
    : {
        appliedToWeekIndex: 0,
        evidenceSufficient: false,
        levelBefore: input.plan.layer,
        recommendedLevel: input.plan.layer,
        levelDelta: 0 as const,
        ratiosBefore,
        recommendedRatios: { ...ratiosBefore },
        changed: false,
        reason: '当前单元沿用阶段起点，过程数据只记录，结业测试后统一生成下一单元安排。',
      }
  const firstRetainedDay = Math.max(0, today.dayIndex - 83)
  const startDate = toCalendarDate(input.weeklyPlanStartDate) as string
  const dailyHistory = Array.from({ length: today.dayIndex - firstRetainedDay + 1 }, (_, offset) => (
    createDailyLearningSummary(input, addCalendarDays(startDate, firstRetainedDay + offset))
  ))
  const weeklyHistory = Array.from({ length: today.weekIndex + 1 }, (_, weekIndex) => (
    createWeeklyLearningReview(input, weekIndex)
  ))
  return { today, currentWeek, previousWeek, adjustment, dailyHistory, weeklyHistory }
}
